package com.tovtalk.ui.platform

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onPlaced
import androidx.compose.ui.layout.positionInRoot
import kotlinx.browser.document
import org.w3c.dom.HTMLIFrameElement

private const val WEBVIEW_CONTAINER_ID = "tovtalk-webview-iframe-container"

@Composable
actual fun TovTalkWebView(
    url: String,
    modifier: Modifier,
    htmlContent: String?
) {
    val container = remember { getOrCreateWebViewContainer() }
    val iframe = remember { getOrCreateIframe(container) }

    DisposableEffect(url, htmlContent) {
        when {
            !htmlContent.isNullOrBlank() -> {
                iframe.removeAttribute("src")
                iframe.srcdoc = htmlContent
            }
            url.isNotBlank() -> {
                iframe.removeAttribute("srcdoc")
                iframe.src = url
            }
            else -> {
                iframe.removeAttribute("src")
                iframe.removeAttribute("srcdoc")
            }
        }
        onDispose {
            container.style.display = "none"
            iframe.removeAttribute("src")
            iframe.removeAttribute("srcdoc")
        }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .onPlaced { coordinates ->
                val size = coordinates.size
                val pos = coordinates.positionInRoot()
                // Compose renders into a <canvas>; offset DOM overlay by the canvas viewport position.
                val (canvasLeft, canvasTop) = canvasViewportOffset()
                positionWebViewContainer(
                    container,
                    (canvasLeft + pos.x).toInt(),
                    (canvasTop + pos.y).toInt(),
                    size.width,
                    size.height
                )
                if (!htmlContent.isNullOrBlank() || url.isNotBlank()) {
                    container.style.display = "block"
                }
            }
    )
}

private fun getOrCreateWebViewContainer(): org.w3c.dom.HTMLElement {
    var container = document.getElementById(WEBVIEW_CONTAINER_ID) as? org.w3c.dom.HTMLElement
    if (container == null) {
        container = document.createElement("div") as org.w3c.dom.HTMLElement
        container.id = WEBVIEW_CONTAINER_ID
        val s = container.asDynamic().style
        s.position = "fixed"
        s.left = "0px"
        s.top = "0px"
        s.width = "0px"
        s.height = "0px"
        s.backgroundColor = "#fff"
        s.zIndex = "10"
        s.overflow = "hidden"
        document.body!!.appendChild(container)
    }
    return container
}

private fun getOrCreateIframe(container: org.w3c.dom.HTMLElement): HTMLIFrameElement {
    var iframe = container.querySelector("iframe") as? HTMLIFrameElement
    if (iframe == null) {
        iframe = document.createElement("iframe") as HTMLIFrameElement
        iframe.style.setProperty("width", "100%")
        iframe.style.setProperty("height", "100%")
        iframe.style.setProperty("border", "none")
        container.appendChild(iframe)
    }
    return iframe
}

private fun positionWebViewContainer(
    container: org.w3c.dom.HTMLElement,
    x: Int,
    y: Int,
    width: Int,
    height: Int
) {
    val s = container.asDynamic().style
    s.left = "${x}px"
    s.top = "${y}px"
    s.width = "${width}px"
    s.height = "${height}px"
}
